import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import debounce from 'lodash/debounce';
import { CREATE_PARTY, CREATE_PARTY_PERMISSION_MESSAGE, CREATE_PERMISSION, DELETE_PERMISSION, MANAGE_PARTY, READ_PERMISSION, UPDATE_PARTY_PERMISSION_MESSAGE, UPDATE_PERMISSION } from './Utils/Constants';
import PartyTable from './Party/PartyTable';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import { CreatePartyDialog } from './Party/CreatePartyDialog';
import { TParty, TCreatePartyPayload } from './Types/party.types';
import { useNavigate } from "react-router";
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Search from 'Components/DesignSystem/Search/Search';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { AppContext } from 'Context/AppContext';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

/**
 * The two page sizes the legacy `<select>` offered. Kept to exactly these two —
 * the sibling list pages offer 10/20/50/100/All, but adding a rung here would change
 * what this screen requests, and this migration is presentational.
 */
const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
];

/** `CustomSearchBar`'s own default (`props.delay ?? 600`), preserved exactly. */
const SEARCH_DEBOUNCE_MS = 600;

/**
 * Manage Parties (`/admin/party/manage`) on the design system (§ 118).
 *
 * Three shared legacy shells are gone from this screen — and only from this screen;
 * each still has other consumers that this change deliberately does not touch:
 *
 * - **`CustomSearchBar`** (Products) → `Search`. The 600ms debounce and the
 *   `trimStart()` were properties of that component, so both move here explicitly
 *   rather than being lost. It also passed `data-testid={props.id}` with no `id`
 *   supplied, so the search box on this page had **no testid** and fell back to the
 *   literal `id="inputID"`.
 * - **`PaginatorWithApi`** (react-paginate) → `Pagination`. The old one hardcoded two
 *   off-palette hexes inside inline SVGs and exposed a bag of anonymous buttons with
 *   no landmark and no `aria-current`. It hid itself entirely below two pages;
 *   `Pagination` keeps the page buttons hidden there and shows the "Showing 1–10 of
 *   n" summary, which is new information rather than a changed control.
 * - **The raw `<button className="button-style">` and `<select>`** → `Button` and
 *   `Dropdown`.
 *
 * **Not changed, on purpose** — these are behaviour, and the brief was presentational
 * (audit findings 13–17, all still owed):
 *
 * - `isMounted` is set `false` in an unmount cleanup and never reset to `true`.
 *   Harmless today because the app mounts no `<StrictMode>`; add one and the cleanup
 *   fires after the first mount, every later `setParties` is skipped, and this page
 *   renders an empty table with a clean typecheck and green tests.
 * - A failed create closes the dialog in both the `done: false` and `catch` branches,
 *   discarding what the user typed. Update keeps its dialog open.
 * - `party-management-create-btn` is never `disabled`; without
 *   `gmsadministration_create` it looks live and refuses after the click, via a toast.
 *   `okf/admin/e2e.md` § 6 claimed the opposite for months — the doc is now corrected
 *   to match this code rather than the code changed to match the doc.
 * - The page-size control and paginator still sit inside
 *   `!showLoader && parties.length > 0`, so they unmount during every fetch.
 */
const ManageParty = () => {
    const navigate = useNavigate();
    const appContext = useContext(AppContext);

    const readPermission = appContext.pm.checkPermission(READ_PERMISSION);
    const createPermission = appContext.pm.checkPermission(CREATE_PERMISSION);
    const updatePermission = appContext.pm.checkPermission(UPDATE_PERMISSION);
    const deletePermission = appContext.pm.checkPermission(DELETE_PERMISSION);

    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({pageNumber: 1, pageSize: 10});
    const [isCreatePartyOpen, setIsCreatePartyOpen] = useState(false);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [parties, setParties] = useState<TParty[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    /* The controlled input's own value. `searchTerm` is the debounced one the fetch
       effect keys on — `CustomSearchBar` held this half internally. */
    const [searchInput, setSearchInput] = useState<string>('');
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<string>('asc');
    const [isCreating, setIsCreating] = useState<boolean>(false);
    
    const [editingPartyDialog, setEditingPartyDialog] = useState<{
        isOpen: boolean;
        party: TParty | null;
    }>({ isOpen: false, party: null });
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    
    const isMounted = useRef(true);
    
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);
    
    useEffect(() => {
        if (!readPermission) {
            navigate("/workspace/module/permission/denied");
            return;
        }
        fetchParties();
    }, [paginationParams, searchTerm, sortBy, sortOrder]);

    const fetchParties = async () => {
        setShowLoader(true);
        try {
            const partyResponse = await ServiceWrapper.doGet( 
                `${api_base_url}user/party/list?page=${paginationParams.pageNumber}&per_page=${paginationParams.pageSize}&sort_by=${sortBy}&sort_order=${sortOrder}&search_string=${searchTerm}`, 
                null, 
                null, 
                AuthManager.getGmsToken()
            ); 
            const response = partyResponse?.data;
            
            if (isMounted.current && response && response.data) {
                setParties(response.data);
                setTotalRecords(response.pagination.total);
            }
        } catch (error) {
            console.error('Error fetching parties:', error);
            if (isMounted.current) {
                toast.error('Failed to fetch parties. Please try again.');
            }
        } finally {
            if (isMounted.current) {
                setShowLoader(false);
            }
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
    };

    /* `CustomSearchBar` built its debounce with `useCallback(debounce(...), [])`.
       Same shape, same 600ms, and now cancelled on unmount — a pending trailing call
       used to land after the page was gone. */
    const debouncedSearch = useMemo(
        () => debounce(handleSearchChange, SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const onSearchInputChange = (value: string) => {
        /* `trimStart()` was `CustomSearchBar`'s, applied to both the visible value and
           the debounced one. Preserved on both. */
        const next = value.trimStart();
        setSearchInput(next);
        debouncedSearch(next);
    };

    const handleCreateParty = async (formData: any) => {
        if (!createPermission) {
            toast.warning(CREATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        setIsCreating(true);
        
        try {
            const payload: TCreatePartyPayload = {
                name: formData.partyName,
                address: formData.address,
                party_type: formData.partyType
            };
            const partyResponse = await ServiceWrapper.doPost( 
                `${api_base_url}party/create`, 
                payload, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = partyResponse?.data;
            if (response && response.done) {
                toast.success('Party created successfully!');
                setIsCreatePartyOpen(false);
                fetchParties();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                if (isMounted.current) {
                    toast.error('Failed to create party. Please try again.');
                    setIsCreatePartyOpen(false);
                }
            }
        } catch (error) {
            console.error('Error creating party:', error);
            if (isMounted.current) {
                toast.error('An error occurred while creating the party.');
                setIsCreatePartyOpen(false);
            }
        } finally {
            if (isMounted.current) {
                setIsCreating(false);
            }
        }
    };

    const handleEditParty = (party: TParty, index: number) => {
        if (!updatePermission) {
            toast.warning(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        setEditingPartyDialog({ 
            isOpen: true, 
            party: party 
        });
    };

    const handleUpdateParty = async (formData: any) => {
        if (!updatePermission) {
            toast.warning(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        
        const party = editingPartyDialog.party;
        if (!party) return;
        
        setIsUpdating(true);
        
        try {
            
            const partyResponse = await ServiceWrapper.doPost( 
                `${api_base_url}party/edit/${party.id}`, 
                formData, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = partyResponse?.data;
            
            if (response && response.done) {
                toast.success('Party updated successfully!');
                setEditingPartyDialog({ isOpen: false, party: null });
                
                await fetchParties();
                
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update party');
            }
        } catch (error) {
            console.error('Error updating party:', error);
            toast.error('An error occurred while updating the party');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleNavigateToDomain = (party: TParty) => {
        navigate(`/admin/party/domain/${party?.id ?? ''}`);
    };

    const handlePageChange = (pageNumber: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    };
    
    const updatePageSize = (pageSize: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
            pageNumber: 1
        }));
    };

    const handleSortChange = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
        setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
    };

    const onCreatePartyClick = () => {
        if (!createPermission) {
            toast.warning(CREATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        setIsCreatePartyOpen(true);
    }

    return (
        <>
            {/* § 267 — the search joins the header instead of owning a row below it.
                Measured at a 1512px viewport on this 1240px page: the header held a
                118px title and a 117px button with 1005px of nothing between them
                (81% of the row), and the search row below it held one 310px control
                with 930px of nothing beside it (75%) — two stacked rows, 1935px of
                combined emptiness, costing 52px of height and pushing the grid to
                y=109. A dedicated row is the right container for a filter SET
                (search + type + date + chips, the way Data Review's toolbar works);
                for a single input above an 81%-empty header it is an empty shelf.
                If filters arrive later, that row comes back and earns its keep.

                `tw-justify-between` is gone with it: it distributed TWO children, and
                a third would have spread all three evenly. The spacer below does the
                job explicitly, which is the same idiom §§ 264-265 use.

                Tab order changes, deliberately and for the better — it was title →
                Create Party → search, because the search lived in a later sibling
                row; it is now title → search → Create Party, which is what the eye
                reads left to right. */}
            <div className="tw-flex tw-items-center tw-border-b tw-border-edge tw-pb-2" data-testid="party-management-header">
                <Typography variant="display" as="h1" className="tw-text-ink" data-testid="party-management-title">
                    {MANAGE_PARTY}
                </Typography>
                {/* `tw-w-72` (288px), not the old `tw-w-[25%]` (310px here): the Guide
                    and Compatibility lists search at 288, and a width that reads off
                    the viewport made this one input a different size on every screen. */}
                <div className="tw-w-72 tw-min-w-0 tw-ms-4">
                    <Search
                        value={searchInput}
                        onChange={onSearchInputChange}
                        /* § 288 — the wait starts at the KEYSTROKE, not at the request.
                           `showLoader` only covers the fetch; `searchInput !== searchTerm`
                           covers the 600ms debounce before it, which is the half of the
                           wait the user is actually staring at. Both values are
                           `trimStart()`ed in `onSearchInputChange`, so they converge. */
                        loading={showLoader || searchInput !== searchTerm}
                        loadingLabel="Searching parties"
                        placeholder="Search"
                        ariaLabel="Search parties"
                        size="md"
                        /* `id="inputID"` is `CustomSearchBar`'s literal fallback, kept so
                           any spec keying off it still resolves. It is a meaningless id
                           shared by every search box in the app that passes no `id` —
                           harmless here because this page renders exactly one. Prefer the
                           testid. */
                        id="inputID"
                        data-testid="party-management-search"
                    />
                </div>
                <div className="tw-flex tw-flex-grow" />
                <Button
                    variant="primary"
                    onClick={onCreatePartyClick}
                    className="tw-w-max tw-whitespace-nowrap tw-shrink-0"
                    data-testid="party-management-create-btn"
                >
                    {CREATE_PARTY}
                </Button>
            </div>
            <PartyTable 
                parties={parties}
                onEditParty={handleEditParty}
                showLoader={showLoader}
                onNavigateToDomain={handleNavigateToDomain}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                onPartyDeleted={fetchParties}
                deletePermission={deletePermission}
                updatePermission={updatePermission}
            />
            {!showLoader && parties.length > 0 ?
                <div id="pagination-container" className="tw-py-2">
                    <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">Show</Typography>
                            <Dropdown
                                label=""
                                aria-label="Records on page select"
                                size="sm"
                                className="tw-w-[84px]"
                                options={PAGE_SIZE_OPTIONS}
                                value={paginationParams?.pageSize ?? 10}
                                onChange={(value) => updatePageSize(Number(value))}
                                id="page-size-select"
                                data-testid="party-management-page-size"
                            />
                        </div>
                        <Pagination
                            page={paginationParams?.pageNumber}
                            totalItems={totalRecords}
                            pageSize={paginationParams?.pageSize}
                            onPageChange={handlePageChange}
                            size="sm"
                            id="paginator"
                            data-testid="party-management-pagination"
                        />
                    </div>
                </div> : null}
            
            <CreatePartyDialog 
                isOpen={isCreatePartyOpen}
                onClose={() => setIsCreatePartyOpen(false)}
                onSave={handleCreateParty}
                isLoading={isCreating}
                mode="create"
            />
            
            <CreatePartyDialog
                isOpen={editingPartyDialog.isOpen}
                onClose={() => setEditingPartyDialog({ isOpen: false, party: null })}
                onSave={handleUpdateParty}
                isLoading={isUpdating}
                editData={editingPartyDialog.party}
                mode="edit"
            />
        </>
    );
}

export default ManageParty;
